import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Instagram, Youtube, Linkedin, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { YOUTUBE_CHANNEL } from '../config/videos';

const Footer = () => {

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Products', href: '/products' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Contact', href: '/contact' },
  ];

  const productLinks = [
    { name: 'AquaCare', href: '/products/aquacare' },
    { name: 'AquaInvoice', href: '/products/aquainvoice' },
    { name: 'AquaBizz', href: '/products/aquabizz' },
    { name: 'AquaLearn', href: '/products/aqualearn' },
    { name: 'AquaStyle', href: '/products/aquastyle' },
  ];

  const importantLinks = [
    { name: 'Documentation', href: '/documentation' },
    { name: 'Technical Architecture', href: '/technical-architecture' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Cookie Settings', href: '#', onClick: () => window.dispatchEvent(new CustomEvent('show-cookie-settings')) },
  ];

  const socialLinks = [
    { name: 'WhatsApp', icon: MessageCircle, href: 'https://wa.me/917017592307', color: 'hover:text-green-500' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/aquargin', color: 'hover:text-pink-500' },
    { name: 'YouTube', icon: Youtube, href: YOUTUBE_CHANNEL.url, color: 'hover:text-red-500' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/aquargin', color: 'hover:text-blue-600' },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <span className="text-2xl font-heading font-bold">Aquargin</span>
            </Link>
            <p className="text-gray-400 leading-relaxed">
              Enterprise-grade automation platform that transforms how businesses operate through AI-powered solutions and no-code development.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <Phone className="w-4 h-4" />
                <span>+91 70175 92307</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>support@aquargin.com</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Products</h3>
            <ul className="space-y-2">
              {productLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200 flex items-center group"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-2 opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Important Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Resources</h3>
            <ul className="space-y-2">
              {importantLinks.map((link) => (
                <li key={link.name}>
                  {link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-left"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Media */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold">Follow Us</h3>
            <p className="text-gray-400 text-sm">
              Stay connected with us on social media for the latest updates and insights.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-400 ${social.color} transition-colors duration-200 p-2 bg-gray-800 rounded-lg hover:bg-gray-700`}
                    aria-label={social.name}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © 2025 Aquargin. All Rights Reserved. 
                <span className="hidden md:inline"> | Enterprise Automation Platform</span>
              </p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end items-center space-x-6 text-xs text-gray-500">
              <Link to="/sitemap.xml" className="hover:text-primary-400 transition-colors">
                Sitemap
              </Link>
              <a 
                href="/robots.txt" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-primary-400 transition-colors"
              >
                Robots.txt
              </a>
              <span>Made with ❤️ in India</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 